"use client";
import React, { useState } from "react";
import { Layout } from "@hcrshubham/core-components";
import { en } from "@hcrshubham/i18n";
import { Alert } from "reactstrap";
import { NextSeo } from 'next-seo';

const ContactPage = () => {
  const [displayBanner, setDisplayBanner] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle Form Submission
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const submitData = new FormData();
    submitData.append("entry.1239197453", formData.name);
    submitData.append("entry.867592170", formData.email);
    submitData.append("entry.1645811488", formData.message);

    try {
      await fetch(
        "https://docs.google.com/forms/u/0/d/e/1FAIpQLSd3uI_C0d775tQyk6yeLwUdvj7f1ZQt_mbksgJg2SnCDiMD2g/formResponse",
        {
          method: "POST",
          body: submitData,
          mode: "no-cors",
        }
      );

      // Show success banner
      setDisplayBanner(true);

      // ✅ Clear form fields properly
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const siteUrl = (process.env.NEXT_PUBLIC_SITE_URL || 'https://shubhamsharma.in').replace(/\/$/, '');
  const getSocialLabel = (href: string) => {
    if (href.includes('github.com')) return 'Open GitHub profile';
    if (href.includes('linkedin.com')) return 'Open LinkedIn profile';
    if (href.includes('instagram.com')) return 'Open Instagram profile';
    return 'Open social profile';
  };


  return (
    <>
      <NextSeo
        title="Contact - Shubham Kumar Sharma"
        description="Get in touch with Shubham Kumar Sharma for collaboration, projects, or inquiries."
        canonical={`${siteUrl}/contact`}
        noindex
        nofollow={false}
      />
    <Layout wrapperClass="main-aboutpage">
      <section className="contact-area">
        <div className="container">
          <div className="gx-row d-flex justify-content-between gap-24">

            {/* Contact Information Section */}
            <div className="contact-infos">
              <h3 data-aos="fade-up">{en.contact.contact.heading}</h3>
              <ul className="contact-details">
                <li className="d-flex align-items-center" data-aos="zoom-in">
                  <div className="icon-box shadow-box">
                    <i className={en.contact.contact.mail.icon} aria-hidden="true" />
                  </div>
                  <div className="right">
                    <span>{en.contact.contact.mail.heading}</span>
                    {en.contact.contact.mail.links.map((item, index) => (
                      <h4 key={index}>
                        <a href={`mailto:${item}`}>{item}</a>
                      </h4>
                    ))}
                  </div>
                </li>
                <li className="d-flex align-items-center" data-aos="zoom-in">
                  <div className="icon-box shadow-box">
                    <i className={en.contact.contact.phone.icon} aria-hidden="true" />
                  </div>
                  <div className="right">
                    <span>{en.contact.contact.phone.heading}</span>
                    {en.contact.contact.phone.links.map((item, index) => (
                      <h4 key={index}>
                        <a href={item.href}>{item.text}</a>
                      </h4>
                    ))}
                  </div>
                </li>
                <li className="d-flex align-items-center" data-aos="zoom-in">
                  <div className="icon-box shadow-box">
                    <i className={en.contact.contact.location.icon} aria-hidden="true" />
                  </div>
                  <div className="right">
                    <span>{en.contact.contact.location.heading}</span>
                    <h4>{en.contact.contact.location.location}</h4>
                  </div>
                </li>
              </ul>

              {/* Social Links */}
              <h3 data-aos="fade-up">{en.contact.profiles.heading}</h3>
              <ul className="social-links d-flex align-center" data-aos="zoom-in">
              <li>
                  <a
                    className="shadow-box"
                    href="https://github.com/hcrshubham/"
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={getSocialLabel('https://github.com/hcrshubham/')}
                  >
                    <i className="iconoir-github" aria-hidden="true" />
                  </a>
                </li>
                <li>
                  <a
                    className="shadow-box"
                    href="https://www.instagram.com/hcrshubham/"
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={getSocialLabel('https://www.instagram.com/hcrshubham/')}
                  >
                    <i className="iconoir-instagram" aria-hidden="true" />
                  </a>
                </li>
                <li>
                  <a
                    className="shadow-box"
                    href="https://www.linkedin.com/in/hcrshubham/"
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={getSocialLabel('https://www.linkedin.com/in/hcrshubham/')}
                  >
                    <i className="iconoir-linkedin" aria-hidden="true" />
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Form Section */}
            <div data-aos="zoom-in" className="contact-form">
              <div className="shadow-box">
                <img src="/assets/bg1.png" alt="BG" className="bg-img" />
                <img src="/assets/icons/icon3.png" alt="Icon"  className="star-icon"/>
                <h1 dangerouslySetInnerHTML={{ __html: en.contact.form.heading }}></h1>

                {/* Form Submission Alert */}
                <Alert
                  className="messenger-box-contact__msg"
                  role="alert"
                  color="success"
                  isOpen={displayBanner}
                  toggle={() => setDisplayBanner(false)}
                >
                  {en.contact.form.onCompletion}
                </Alert>

                {/* Form */}
                <form onSubmit={handleFormSubmit}>
                  <div className="input-group">
                    <input
                      type="text"
                      name="name"
                      id="name"
                      aria-label="Your name"
                      autoComplete="name"
                      required
                      placeholder="Name *"
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="input-group">
                    <input
                      id="email"
                      type="email"
                      name="email"
                      aria-label="Your email address"
                      autoComplete="email"
                      required
                      placeholder="Email *"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="input-group">
                    <textarea
                      name="message"
                      id="message"
                      aria-label="Your message"
                      required
                      placeholder="Message *"
                      value={formData.message}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="input-group">
                    <button className="theme-btn submit-btn" type="submit">
                      {en.contact.form.button.text}
                    </button>
                  </div>
                </form>
              </div>
            </div>

          </div>
        </div>
      </section>
    </Layout>
    </>
  );
};

export default ContactPage;
